/**
 * Hand-written types for the admin venue directory (list + detail).
 *
 * Matches the contracts in `docs/api-requirements.md` API-1.17 (list)
 * and API-1.18 (detail). We don't import the Orval-generated surface —
 * see CLAUDE.md → TD-07 — and we don't cross-import the enums from
 * `features/venues-approval` (golden rule #3), so they're redefined
 * here. They mirror the `VenueStatus` / `VenueType` enums in the API's
 * `schema.prisma`.
 */

export const VenueType = {
  GYM: "GYM",
  SPA: "SPA",
  GYM_AND_SPA: "GYM_AND_SPA",
} as const;
export type VenueType = (typeof VenueType)[keyof typeof VenueType];

export const VenueStatus = {
  PENDING_APPROVAL: "PENDING_APPROVAL",
  ACTIVE: "ACTIVE",
  SUSPENDED: "SUSPENDED",
  REJECTED: "REJECTED",
  CHANGES_REQUESTED: "CHANGES_REQUESTED",
} as const;
export type VenueStatus = (typeof VenueStatus)[keyof typeof VenueStatus];

/** `all` is a UI-only sentinel — never sent to the backend. */
export type VenueStatusFilter = VenueStatus | "all";
export type VenueTypeFilter = VenueType | "all";

/** Row shape returned by `GET /api/admin/venues` (paginated list). */
export interface VenueListItem {
  readonly id: string;
  readonly name: string;
  readonly type: VenueType;
  readonly address: string;
  readonly city: string;
  readonly state: string;
  readonly country: string;
  readonly status: VenueStatus;
  readonly ownerId: string;
  /**
   * ADR-0009 prod test partition. Stamped from the owner's
   * `User.isTestAccount` AT CREATION and IMMUTABLE afterwards — there is no
   * edit path, by design: the append-only financial history behind an entity
   * can never be re-partitioned. Read-only in the backoffice; renders the
   * TEST badge and nothing else.
   *
   * The API declares this REQUIRED on the admin list + detail models (api
   * `1187c5b`). Optional here purely as a runtime guard against an API build
   * that predates ADR-0009, where the field is absent. An absent flag means
   * "real" — never "test".
   */
  readonly isTest?: boolean;
  readonly ownerEmail: string;
  readonly ownerName: string;
  readonly createdAt: string;
}

export interface VenuesListFilters {
  readonly query: string;
  readonly status: VenueStatusFilter;
  readonly type: VenueTypeFilter;
  readonly page: number;
  readonly limit: number;
}

export interface VenuesListResponse {
  readonly items: readonly VenueListItem[];
  readonly totalCount: number;
  readonly page: number;
  readonly limit: number;
}

export interface VenueOwnerSummary {
  readonly id: string;
  readonly email: string;
  readonly displayName: string;
}

export interface VenueHours {
  readonly dayOfWeek: number;
  readonly openTime: string;
  readonly closeTime: string;
  readonly isClosed: boolean;
}

export interface VenueAmenity {
  readonly id: string;
  readonly category: string;
  readonly slug: string;
  readonly name: string;
}

/** Detail shape returned by `GET /api/admin/venues/:id` (API-1.18). */
export interface VenueDetail {
  readonly id: string;
  /**
   * ADR-0009 prod test partition. Stamped from the owner's
   * `User.isTestAccount` AT CREATION and IMMUTABLE afterwards — there is no
   * edit path, by design: the append-only financial history behind an entity
   * can never be re-partitioned. Read-only in the backoffice; renders the
   * TEST badge and nothing else.
   *
   * The API declares this REQUIRED on the admin list + detail models (api
   * `1187c5b`). Optional here purely as a runtime guard against an API build
   * that predates ADR-0009, where the field is absent. An absent flag means
   * "real" — never "test".
   */
  readonly isTest?: boolean;
  readonly name: string;
  readonly type: VenueType;
  readonly address: string;
  readonly city: string;
  readonly state: string;
  readonly zipCode: string;
  readonly country: string;
  readonly latitude: number;
  readonly longitude: number;
  readonly description?: string | null;
  readonly photos: readonly string[];
  readonly status: VenueStatus;
  readonly owner: VenueOwnerSummary;
  readonly hours: readonly VenueHours[];
  readonly amenities: readonly VenueAmenity[];
  readonly createdAt: string;
  readonly updatedAt: string;
}
